import {
  motion,
  AnimatePresence,
  TargetAndTransition,
  Target,
} from 'framer-motion';
import styles from './component-animation.module.scss';
import { useState } from 'react';
import classNames from 'classnames';

export enum AnimationTypes {
  EXPAND_DOWN_AND_FADE_IN,
  FADE_IN,
  FADE_IN_SHRINK_OUT,
  SLIDE_UP_AND_FADE_IN,
  SLIDE_UP_OFF_PAGE_AND_FADE_IN,
}

type AnimationConfig = {
  start: Target;
  end: TargetAndTransition;
  exit?: TargetAndTransition;
  className?: string;
};

type AnimationDefType = {
  [key in AnimationTypes]: AnimationConfig;
};

const AnimationDef: AnimationDefType = {
  [AnimationTypes.EXPAND_DOWN_AND_FADE_IN]: {
    start: {
      height: 0,
      opacity: 0,
      overflow: 'hidden',
    },
    end: {
      height: 'auto',
      opacity: 1,
      transitionEnd: {
        overflow: 'unset',
      },
    },
    className: styles['expand-down-and-fade-in'],
  },
  [AnimationTypes.FADE_IN]: {
    start: {
      opacity: 0,
    },
    end: {
      opacity: 1,
    },
  },
  [AnimationTypes.FADE_IN_SHRINK_OUT]: {
    start: {
      opacity: 0,
    },
    end: {
      opacity: 1,
    },
    exit: {
      height: 0,
      overflow: 'hidden',
    },
  },
  [AnimationTypes.SLIDE_UP_AND_FADE_IN]: {
    start: {
      y: 50,
      opacity: 0,
    },
    end: {
      y: 0,
      opacity: 1,
    },
  },
  [AnimationTypes.SLIDE_UP_OFF_PAGE_AND_FADE_IN]: {
    start: {
      y: '100vh',
      opacity: 0,
    },
    end: {
      y: 0,
      opacity: 1,
    },
  },
};

type Props = {
  displayContentIf?: boolean;
  type: AnimationTypes;
  duration?: number;
  delay?: number;
  children: React.ReactNode;
  id: string;
  onAnimationEnd?: () => void;
  customAnimation?: AnimationConfig;
  className?: string;
};

const ComponentAnimation = ({
  displayContentIf = true,
  type,
  duration = 0.3,
  delay = 0,
  children,
  id,
  onAnimationEnd,
  customAnimation,
  className,
}: Props) => {
  const [animationIsComplete, setAnimationIsComplete] = useState(false);

  const animationConfig = customAnimation || AnimationDef[type];

  const onAnimationCompleteHandler = () => {
    setAnimationIsComplete(true);
    onAnimationEnd?.();
  };

  const onAnimationStartHandler = () => {
    setAnimationIsComplete(false);
  };

  return (
    <AnimatePresence>
      {displayContentIf && (
        <motion.div
          className={classNames(
            className,
            !animationIsComplete && animationConfig.className,
          )}
          onAnimationComplete={onAnimationCompleteHandler}
          onAnimationStart={onAnimationStartHandler}
          initial={animationConfig.start}
          animate={animationConfig.end}
          exit={animationConfig.exit || animationConfig.start}
          transition={{
            duration: duration,
            delay: delay,
          }}
          key={`${id}-motion`}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ComponentAnimation;
